/**
 * Sport selection prompt frequency (Global Sport Mode).
 * UI must call through ActiveSport / shouldPromptActiveSportSelection — never embed timing.
 *
 * Phase 2.4 temporary: EVERY_COLD_START (browser refresh / app restart).
 * Flip CURRENT_SPORT_SELECTION_FREQUENCY later without touching overlay components.
 */

/** @typedef {'once' | 'every_cold_start' | 'daily' | 'every_2_days' | 'weekly'} SportSelectionFrequencyMode */

export const SPORT_SELECTION_FREQUENCY = Object.freeze({
  /** Only when the player has never chosen a sport. */
  ONCE: "once",
  /** Phase 2.4 temporary — every fresh JS session (refresh / restart). */
  EVERY_COLD_START: "every_cold_start",
  DAILY: "daily",
  EVERY_2_DAYS: "every_2_days",
  WEEKLY: "weekly",
});

/**
 * Product knob — change this alone to alter prompt cadence.
 * @type {SportSelectionFrequencyMode}
 */
export const CURRENT_SPORT_SELECTION_FREQUENCY =
  SPORT_SELECTION_FREQUENCY.EVERY_COLD_START;

const SESSION_ACK_KEY = "tra.sportSelectionSessionAck";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * @returns {boolean}
 */
export function hasAcknowledgedSportSelectionThisSession() {
  if (typeof window === "undefined" || !window.sessionStorage) return false;
  try {
    return window.sessionStorage.getItem(SESSION_ACK_KEY) === "1";
  } catch {
    return false;
  }
}

export function acknowledgeSportSelectionThisSession() {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(SESSION_ACK_KEY, "1");
  } catch {
    /* private mode */
  }
}

/**
 * Pure frequency decision — no storage reads except optional sessionAck override.
 * @param {{
 *   mode?: SportSelectionFrequencyMode,
 *   hasChosen?: boolean,
 *   selectedAt?: number | null,
 *   sessionAcked?: boolean,
 *   now?: number,
 * }} [input]
 * @returns {boolean}
 */
export function shouldPromptSportSelection(input = {}) {
  const mode = input.mode ?? CURRENT_SPORT_SELECTION_FREQUENCY;
  const now = input.now ?? Date.now();
  const chosen = Boolean(input.hasChosen);
  const sessionAcked =
    input.sessionAcked !== undefined
      ? Boolean(input.sessionAcked)
      : hasAcknowledgedSportSelectionThisSession();

  if (mode === SPORT_SELECTION_FREQUENCY.ONCE) {
    return !chosen;
  }

  if (mode === SPORT_SELECTION_FREQUENCY.EVERY_COLD_START) {
    if (!chosen) return true;
    return !sessionAcked;
  }

  if (!chosen) return true;

  const selectedAt = input.selectedAt;
  if (selectedAt == null) return true;

  const age = now - selectedAt;
  if (mode === SPORT_SELECTION_FREQUENCY.DAILY) return age >= DAY_MS;
  if (mode === SPORT_SELECTION_FREQUENCY.EVERY_2_DAYS) return age >= 2 * DAY_MS;
  if (mode === SPORT_SELECTION_FREQUENCY.WEEKLY) return age >= 7 * DAY_MS;

  return !chosen;
}
